import React, { useState, useContext, useEffect, useId, useRef } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	IconButton,
	Stack
} from '@mui/material';
import { isEmpty } from 'lodash';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

import { getToken } from '../../api/Account';
import { DataContext } from '../../context/DataContext';
import {
	getCommunitiesByMunAsync,
	getMunicipalitiesByDeptopAsync,
	getOnlyDepartmentListAsync
} from '../../api/Locations';
import { getRuta, simpleMessage } from '../../helpers/Helpers';
import { tienePermiso } from '../../helpers/permisos';
import { addClientAsync } from '../../api/Clients';
import {
	EstadoError,
	PageHeader,
	SeccionFormulario,
	SelectorFotos,
	useAviso
} from '../../components/base';
import {
	CargandoFormularioCliente,
	ERROR_OTRA_SESION,
	FormularioCliente,
	llevarAlPrimerError,
	nombreCompleto,
	validarCliente
} from './comunes';

const VALORES_INICIALES = {
	firstName: '',
	secondName: '',
	lastName: '',
	secondLastName: '',
	identification: '',
	phone: '',
	sex: '',
	civilStatus: '',
	department: '',
	municipality: '',
	community: '',
	address: '',
	actividadEconomica: '',
	company: '',
	companyAddress: ''
};

const AddClient = () => {
	const token = getToken();
	let ruta = getRuta();
	let navigate = useNavigate();
	const prefijo = useId();

	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
	const aviso = useAviso();

	const [departmentList, setDepartmentList] = useState([]);
	const [MunicipalitiesList, setMunicipalitiesList] = useState([]);
	const [communitiesList, setCommunitiesList] = useState([]);
	const [cargandoUbicacion, setCargandoUbicacion] = useState({});

	const [valores, setValores] = useState(VALORES_INICIALES);

	const [selectedFiles, setSelectedFiles] = useState([]);
	const [previews, setPreviews] = useState([]);

	// Carga de los departamentos del arranque: mientras llegan se ve un esqueleto del formulario,
	// y si falla se dice y se puede reintentar.
	const [cargando, setCargando] = useState(true);
	const [errorCarga, setErrorCarga] = useState(false);
	// Errores de validación junto a cada campo: { campo: 'texto' }.
	const [errores, setErrores] = useState({});
	// El overlay de carga no bloquea el teclado: el ref corta un segundo envío al instante
	// y el estado deshabilita el botón (misma protección que AddCredit y AddAbono).
	const enviando = useRef(false);
	const [guardando, setGuardando] = useState(false);
	const formulario = useRef(null);
	// Respuestas de la cascada que llegan tarde (se eligió otra opción mientras tanto) se descartan.
	const peticionMunicipios = useRef(0);
	const peticionComunidades = useRef(0);

	// El cliente recién guardado: ofrece ir a verlo o crearle un crédito (el formulario se limpia, como siempre).
	const [creado, setCreado] = useState(null);
	const avisoCreado = useRef(null);

	const cargarDepartamentos = async () => {
		setCargando(true);
		setErrorCarga(false);
		setIsLoading(true);
		try {
			const result = await getOnlyDepartmentListAsync(token);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				setCargando(false);
				setErrorCarga(true);
				if (result.error === ERROR_OTRA_SESION) {
					simpleMessage(result.error, 'error');
				}
				return;
			}

			setDepartmentList(result.data.result);
			setIsLoading(false);
			setCargando(false);
		} catch (e) {
			// Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
			setIsLoading(false);
			setCargando(false);
			setErrorCarga(true);
		}
	};

	useEffect(() => {
		cargarDepartamentos();
	}, []);

	useEffect(() => {
		if (creado && avisoCreado.current && typeof avisoCreado.current.scrollIntoView === 'function') {
			avisoCreado.current.scrollIntoView({ block: 'start' });
		}
	}, [creado]);

	// Escribir o elegir en un campo limpia su error.
	const cambiar = (campo, valor) => {
		setValores((actual) => ({ ...actual, [campo]: valor }));
		if (errores[campo]) setErrores({ ...errores, [campo]: undefined });
	};

	const addNewClient = async () => {
		if (enviando.current) {
			return;
		}
		// Mismas reglas de siempre, en el mismo orden; ahora se muestran todas juntas, cada una
		// junto a su campo, en vez de un aviso a la vez.
		const faltantes = validarCliente(valores, {
			conFotos: true,
			cantidadFotos: selectedFiles.length
		});
		setErrores(faltantes);
		if (!isEmpty(faltantes)) {
			llevarAlPrimerError(formulario);
			return;
		}

		enviando.current = true;
		setGuardando(true);
		const { sex, civilStatus } = valores;
		const formData = new FormData();

		// Agregar datos del cliente como campos de texto
		formData.append('firstName', valores.firstName);
		formData.append('secondName', valores.secondName);
		formData.append('lastName', valores.lastName);
		formData.append('secondLastName', valores.secondLastName);
		formData.append('identification', valores.identification);
		formData.append('phone', valores.phone);
		formData.append('sex', sex === 0 ? 'MASCULINO' : 'FEMENINO');
		formData.append(
			'civilStatus',
			civilStatus === 0
				? sex === 0
					? 'SOLTERO'
					: 'SOLTERA'
				: civilStatus === 1
				? sex === 0
					? 'CASADO'
					: 'CASADA'
				: 'UNION LIBRE'
		);
		formData.append('idCommunity', valores.community);
		formData.append('address', valores.address);
		formData.append('company', valores.company);
		formData.append('companyAddress', valores.companyAddress);
		formData.append('actividadEconomica', valores.actividadEconomica);

		selectedFiles.forEach((photo) => {
			formData.append(`clientPhotos`, photo);
		});

		setIsLoading(true);
		let result;
		try {
			result = await addClientAsync(token, formData);
		} catch (e) {
			enviando.current = false;
			setGuardando(false);
			setIsLoading(false);
			aviso.error('No se pudo conectar con el servidor. Inténtalo de nuevo.');
			return;
		}
		enviando.current = false;
		setGuardando(false);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}

			aviso.error(result.error || 'No se pudo guardar el cliente');
			return;
		}

		setIsLoading(false);
		const guardado = result.data?.result;
		setCreado({
			id: guardado?.id,
			nombre: (guardado?.fullName || nombreCompleto(valores)).replace(/\s+/g, ' ').trim()
		});
		clearData();
		aviso.exito('Cliente guardado');
	};

	const handleChangeDepartment = async (event) => {
		const valor = event.target.value;
		const peticion = ++peticionMunicipios.current;
		++peticionComunidades.current;
		setMunicipalitiesList([]);
		setCommunitiesList([]);
		setCargandoUbicacion({});
		cambiar('department', valor);

		if (valor !== '') {
			setIsLoading(true);
			setCargandoUbicacion({ municipios: true });
			try {
				const result = await getMunicipalitiesByDeptopAsync(token, valor);
				if (peticion !== peticionMunicipios.current) return;
				setCargandoUbicacion({});
				if (!result.statusResponse) {
					setIsLoading(false);
					if (result.error === 'Unauthorized') {
						setIsUnautorized(true);
						return;
					}
					aviso.error(result.error || 'No se pudieron cargar los municipios');
					return;
				}
				setMunicipalitiesList(result.data.result);
				setIsLoading(false);
			} catch (e) {
				if (peticion !== peticionMunicipios.current) return;
				setCargandoUbicacion({});
				setIsLoading(false);
				aviso.error('No se pudieron cargar los municipios. Revisa tu conexión.');
			}
		} else {
			setMunicipalitiesList([]);
		}
	};

	const handleChangeMunicipality = async (event) => {
		const valor = event.target.value;
		const peticion = ++peticionComunidades.current;
		setCommunitiesList([]);
		setCargandoUbicacion({});
		cambiar('municipality', valor);

		if (valor !== '') {
			setIsLoading(true);
			setCargandoUbicacion({ comunidades: true });
			try {
				const result = await getCommunitiesByMunAsync(token, valor);
				if (peticion !== peticionComunidades.current) return;
				setCargandoUbicacion({});
				if (!result.statusResponse) {
					setIsLoading(false);
					if (result.error === 'Unauthorized') {
						setIsUnautorized(true);
						return;
					}
					aviso.error(result.error || 'No se pudieron cargar las comunidades');
					return;
				}
				setCommunitiesList(result.data.result);
				setIsLoading(false);
			} catch (e) {
				if (peticion !== peticionComunidades.current) return;
				setCargandoUbicacion({});
				setIsLoading(false);
				aviso.error('No se pudieron cargar las comunidades. Revisa tu conexión.');
			}
		} else {
			setCommunitiesList([]);
		}
	};

	const clearData = () => {
		setValores(VALORES_INICIALES);
		setSelectedFiles([]);
		setPreviews([]);
		setErrores({});
	};

	const onChangeFile = (e) => {
		const files = Array.from(e.target.files);

		if (!files) return;

		// Crear vistas previas
		const newPreviews = files.map((file) => ({
			file,
			preview: URL.createObjectURL(file)
		}));

		setSelectedFiles(files);
		setPreviews(newPreviews);
		if (errores.fotos) setErrores({ ...errores, fotos: undefined });
		// Permite volver a elegir el mismo archivo después de quitarlo.
		e.target.value = '';
	};

	const removeImage = (index) => {
		// Revocar la URL para liberar memoria
		URL.revokeObjectURL(previews[index].preview);

		const newPreviews = previews.filter((_, i) => i !== index);
		const newFiles = selectedFiles.filter((_, i) => i !== index);

		setPreviews(newPreviews);
		setSelectedFiles(newFiles);
	};

	// Limpiar URLs cuando el componente se desmonte
	React.useEffect(() => {
		return () => {
			previews.forEach((preview) => URL.revokeObjectURL(preview.preview));
		};
	}, []);

	const puedeVerCliente = creado?.id !== undefined && tienePermiso(access, 'CLIENTS VER');
	const puedeCrearCredito =
		creado?.id !== undefined && tienePermiso(access, 'CREDITS CREATE');

	return (
		<div>
			<Container maxWidth={false} disableGutters>
				<PageHeader
					titulo="Nuevo cliente"
					subtitulo="Registra sus datos, su ubicación y sus fotos"
					icono={<FontAwesomeIcon icon={faUserPlus} />}
					acciones={
						<Button
							variant="outlined"
							startIcon={<ArrowBackIcon />}
							onClick={() => {
								navigate(`${ruta}/clients`);
							}}
						>
							Regresar
						</Button>
					}
				/>

				{creado ? (
					<Alert
						ref={avisoCreado}
						severity="success"
						role="status"
						sx={(theme) => ({
							mb: 3,
							alignItems: 'center',
							backgroundColor: theme.palette.soft.success,
							color: theme.palette.text.primary,
							border: `1px solid ${theme.palette.success.main}`,
							'& .MuiAlert-icon': { color: theme.palette.success.main },
							'& .MuiAlert-action': { flexWrap: 'wrap', gap: 1, pt: 0 }
						})}
						action={
							<>
								{puedeVerCliente ? (
									<Button
										size="small"
										variant="outlined"
										onClick={() => navigate(`${ruta}/client/${creado.id}`)}
									>
										Ver cliente
									</Button>
								) : null}
								{puedeCrearCredito ? (
									<Button
										size="small"
										variant="contained"
										onClick={() => navigate(`${ruta}/credit/add/${creado.id}`)}
									>
										Crear crédito
									</Button>
								) : null}
								<IconButton
									size="small"
									aria-label="Cerrar el aviso"
									onClick={() => setCreado(null)}
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</>
						}
					>
						{`Cliente ${creado.nombre} guardado.`}
					</Alert>
				) : null}

				{errorCarga ? (
					<EstadoError
						titulo="No se pudo cargar el formulario"
						mensaje="Faltan datos para crear el cliente. Revisa tu conexión e inténtalo de nuevo."
						onReintentar={cargarDepartamentos}
					/>
				) : cargando ? (
					<CargandoFormularioCliente etiqueta="Cargando el formulario…" />
				) : (
					<Stack spacing={3} ref={formulario}>
						<FormularioCliente
							prefijo={prefijo}
							valores={valores}
							errores={errores}
							onCambio={cambiar}
							onDepartamento={handleChangeDepartment}
							onMunicipio={handleChangeMunicipality}
							listas={{
								departamentos: departmentList,
								municipios: MunicipalitiesList,
								comunidades: communitiesList
							}}
							cargandoUbicacion={cargandoUbicacion}
						/>

						<SeccionFormulario
							titulo="Fotos"
							descripcion="Fotos del cliente: se necesita al menos una."
						>
							<SelectorFotos
								previews={previews}
								onChange={onChangeFile}
								onRemove={removeImage}
								error={errores.fotos}
								disabled={guardando}
							/>
						</SeccionFormulario>

						<Box
							sx={{
								display: 'flex',
								justifyContent: { xs: 'stretch', sm: 'flex-end' }
							}}
						>
							<Button
								variant="contained"
								size="large"
								disabled={guardando}
								onClick={() => addNewClient()}
								startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
								sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 220 }}
							>
								{guardando ? 'Guardando…' : 'Guardar cliente'}
							</Button>
						</Box>
					</Stack>
				)}
			</Container>
		</div>
	);
};

export default AddClient;
